import { fetchAuthSession } from 'aws-amplify/auth';
import React, { Suspense } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Route, Router, Switch } from 'react-router-dom';
import { createGlobalStyle } from 'styled-components';

import { refreshFleet } from '../actions/typedActions';
import { APP_ROUTES } from '../constants/routes';
import { useAssetManagement, useFleet, useLocalStorage } from '../hooks';
import { ErrorBoundary, LoadingOverlay, TitleUpdater } from './atoms';
import { history } from './history';
import { OauthIDPResponse } from './login/OauthIDPResponse';
import { basic } from '../theme/colors';

const KeyboardController = React.lazy(() => import('./keyboardControl/keyboardController'));
const SafeSenseConsole = React.lazy(() => import('./SafeSenseConsole'));

const MFASetup = React.lazy(() => import('./login/MFASetup'));
const SMSSetup = React.lazy(() => import('./login/SMSSetup'));
const ChangePassword = React.lazy(() => import('./login/ChangePassword'));
const ForgotPassword = React.lazy(() => import('./login/ForgotPassword'));

const ForgotPasswordConfirmation = React.lazy(() => import('./login/ForgotPasswordConfirmation'));
const Login = React.lazy(() => import('./login/Login'));

function AppContainer() {
  const [userIsLogged, setUserIsLogged] = useLocalStorage('userLogged', false);
  const isAuthenticated = useSelector((state) => state.store.isAuthenticated);
  const dispatch = useDispatch();
  const fleet = useFleet();
  const isAssetManagement = useAssetManagement();

  const authFn = async () => {
    try {
      const session = await fetchAuthSession();
      const { idToken } = session.tokens ?? {};

      if (idToken) {
        dispatch(refreshFleet(true));
        setUserIsLogged(true);
      } else {
        setUserIsLogged(false);
      }
    } catch (err) {
      setUserIsLogged(false);
    }
  };

  React.useEffect(() => {
    function handleTab(e) {
      if (e.key === 'Tab') {
        document.body.classList.add('using-keyboard');
      }
    }

    // Add handler to remove using-keyboard class on mouse click
    function handleMouseDown() {
      document.body.classList.remove('using-keyboard');
    }

    window.addEventListener('keydown', handleTab);
    window.addEventListener('mousedown', handleMouseDown);

    return () => {
      window.removeEventListener('keydown', handleTab);
      window.removeEventListener('mousedown', handleMouseDown);
    };
  }, []);

  React.useEffect(() => {
    authFn();
  }, []);

  React.useEffect(() => {
    if (isAssetManagement && window.location.pathname === APP_ROUTES.root) {
      history.replace(APP_ROUTES.portalMap);
    }
  }, [fleet?.uuid]);

  // If user is logged in but we're still fetching some user-related data (like fleet)
  if (!isAuthenticated && userIsLogged) {
    return <LoadingOverlay />;
  }

  // If user is not logged in, show login flow
  if (!isAuthenticated) {
    return (
      <Router history={history}>
        <ErrorBoundary>
          <TitleUpdater />
          <GlobalStyles />
          <main>
            <Switch>
              <Route exact path={APP_ROUTES.oauthCallback}>
                <OauthIDPResponse />
              </Route>

              <Route exact path={APP_ROUTES.forgotPassword}>
                <Suspense fallback={<LoadingOverlay />}>
                  <ForgotPassword />
                </Suspense>
              </Route>

              <Route exact path={APP_ROUTES.setupNewPassword}>
                <Suspense fallback={<LoadingOverlay />}>
                  <ForgotPasswordConfirmation />
                </Suspense>
              </Route>

              <Route exact path={APP_ROUTES.changePassword}>
                <Suspense fallback={<LoadingOverlay />}>
                  <ChangePassword />
                </Suspense>
              </Route>

              <Route path={APP_ROUTES.setupMFA}>
                <Suspense fallback={<LoadingOverlay />}>
                  <MFASetup />
                </Suspense>
              </Route>

              <Route path={APP_ROUTES.setupMFASMS}>
                <Suspense fallback={<LoadingOverlay />}>
                  <SMSSetup />
                </Suspense>
              </Route>

              <Route>
                <Suspense fallback={<LoadingOverlay />}>
                  <Login />
                </Suspense>
              </Route>
            </Switch>
          </main>
        </ErrorBoundary>
      </Router>
    );
  }

  return (
    <ErrorBoundary>
      <GlobalStyles />

      <Suspense fallback={<LoadingOverlay />}>
        <KeyboardController />
        <SafeSenseConsole />
      </Suspense>
    </ErrorBoundary>
  );
}

export default AppContainer;

const GlobalStyles = createGlobalStyle`
.gm-style div[role=menuitemcheckbox] {
  line-height: 1em;
  vertical-align: middle;
}

.gm-style div[role=menuitemcheckbox] img {
  transform: none !important;
  margin-right: 0.25em !important;
}

.gm-style div[role=menuitemcheckbox] label {
  margin-bottom: 0;
  position: relative;
  top: 2px;
  padding-top: 0.5em;
  padding-bottom: 0.5em;
}

.gm-style ul[role=menu] {
  min-width: 100%;
  text-align: center;
  font-size: 1rem;
}

body:not(.using-keyboard) *:focus-visible {
  outline: none !important;
}

body.using-keyboard *:focus {
  outline: none !important;
  transition: 200ms linear all;
  box-shadow: 0 0 2px 2px ${basic.haydenBlueGray} !important;
}

/* Styles for high contrast mode users */
@media (forced-colors: active) {
  *:focus-visible,
  button:focus, 
  a:focus, 
  input:focus, 
  select:focus, 
  textarea:focus,
  [role="button"]:focus, 
  [tabindex]:focus {
    outline: 3px solid ${basic.haydenAccent} !important;
    outline-offset: 2px !important;
  }
}

/* Styles for users who prefer increased contrast */
@media (prefers-contrast: more) {
  *:focus-visible {
    outline: 3px solid #0000FF !important;
    outline-offset: 2px !important;
    box-shadow: 0 0 0 3px rgba(0, 0, 255, 0.5) !important;
  }
  
  button:focus, 
  a:focus, 
  input:focus, 
  select:focus, 
  textarea:focus,
  [role="button"]:focus, 
  [tabindex]:focus {
    outline: 3px solid #0000FF !important;
    outline-offset: 2px !important;
    box-shadow: 0 0 0 3px rgba(0, 0, 255, 0.5) !important;
  }
  
  *:active {
    opacity: 0.7 !important;
  }
}
`;
